import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { NEET_PHYSICS_CH6_QUESTIONS } from "../data/neetPhysicsCh6Questions";

// ─────────────────────────────────────────────────────────────────────────────
// NEET Physics — Chapter 6: Centre of Mass and System of Particles (NCERT
// Class 11, "Systems of Particles and Rotational Motion" — this chapter
// covers only the centre-of-mass / linear-momentum-of-system half; rotational
// dynamics — torque, moment of inertia, angular momentum — is Chapter 7.)
// Content is drafted from the NCERT syllabus topic list; each concept has an
// explanation plus a worked example. NOT YET human-reviewed by a subject-matter
// expert — treat as a first draft and verify against NCERT before exam use.
// ─────────────────────────────────────────────────────────────────────────────

const SECTIONS = ["Centre of Mass — Discrete Systems", "Centre of Mass — Continuous Bodies", "Motion of the Centre of Mass", "Momentum Conservation Applications"];

const SECTION_COLORS = {
  "Centre of Mass — Discrete Systems":     "#0891b2",
  "Centre of Mass — Continuous Bodies":    "#ca8a04",
  "Motion of the Centre of Mass":          "#c026d3",
  "Momentum Conservation Applications":    "#ea580c",
};

const CONCEPTS = [
  // ── Centre of Mass — Discrete Systems ──────────────────────────────────────
  { section: "Centre of Mass — Discrete Systems", title: "Centre of mass of a two-particle system",
    explanation: "For two particles of masses m1 and m2 at positions x1 and x2 on a line, the centre of mass (CM) is located at x_cm = (m1x1 + m2x2)/(m1+m2). It is a mass-weighted average of the positions, so the CM always lies closer to the heavier particle. In vector form, r_cm = (m1r1 + m2r2)/(m1+m2).",
    example: `m1 = 2 kg at x1 = 0 m
m2 = 3 kg at x2 = 5 m

x_cm = (2(0) + 3(5)) / (2+3) = 15/5 = 3 m
(CM lies closer to the heavier mass m2, as expected)` },
  { section: "Centre of Mass — Discrete Systems", title: "Centre of mass of a system of n particles",
    explanation: "For n particles of masses m1, m2, ..., mn with position vectors r1, r2, ..., rn, the centre of mass position vector is r_cm = (sum of mi.ri) / (sum of mi) = (sum of mi.ri)/M, where M is the total mass. This single vector equation gives three independent scalar equations for the x, y, z coordinates.",
    example: `Three particles: m1=1kg at (0,0); m2=2kg at (2,0); m3=3kg at (0,3)
M = 1+2+3 = 6 kg

x_cm = (1x0 + 2x2 + 3x0)/6 = 4/6 = 0.67 m
y_cm = (1x0 + 2x0 + 3x3)/6 = 9/6 = 1.5 m

CM is at (0.67 m, 1.5 m)` },
  { section: "Centre of Mass — Discrete Systems", title: "Position of CM relative to individual particles (two-body case)",
    explanation: "For a two-particle system separated by distance d, the CM divides the line joining them such that the distances from the CM satisfy m1.r1 = m2.r2, where r1 and r2 are the distances of the CM from m1 and m2 respectively (r1 + r2 = d). The CM therefore lies closer to the larger mass, and coincides with the geometric midpoint only when the two masses are equal.",
    example: `m1 = 4 kg, m2 = 6 kg, separated by d = 10 m

r1 (distance of CM from m1) = m2.d/(m1+m2) = 6x10/10 = 6 m
r2 (distance of CM from m2) = m1.d/(m1+m2) = 4x10/10 = 4 m

Check: r1 + r2 = 6 + 4 = 10 m (matches d)
Check: m1.r1 = 4x6 = 24, m2.r2 = 6x4 = 24  <- equal, confirms CM location
(CM lies closer to m2, the heavier mass)` },

  // ── Centre of Mass — Continuous Bodies ─────────────────────────────────────
  { section: "Centre of Mass — Continuous Bodies", title: "Centre of mass of a uniform rod",
    explanation: "For continuous bodies the discrete sum becomes an integral, but for a body with uniform density and a symmetric shape the CM can be located directly by symmetry, with no calculus needed. A uniform (thin) rod has its mass spread evenly along its length, so by symmetry its CM lies at its geometric centre -- the midpoint of the rod.",
    example: `Uniform rod of length L = 2 m, mass 4 kg, lying along the
x-axis from x=0 to x=2 m

By symmetry, CM is at the midpoint: x_cm = L/2 = 1 m
(True regardless of the rod's mass, as long as density is
uniform along its length)` },
  { section: "Centre of Mass — Continuous Bodies", title: "Centre of mass of symmetric bodies — ring, disc, and sphere",
    explanation: "For any uniform body possessing a centre of symmetry, the CM lies exactly at that geometric centre. A uniform circular ring, a uniform circular disc, and a uniform solid (or hollow) sphere are each symmetric about their centre, so the CM of each lies exactly there -- even for a ring, where no mass actually exists at that central point.",
    example: `Uniform ring, radius R = 0.5 m, centered at origin:
   CM at (0,0) -- even though the ring has no material there

Uniform disc, radius R, centered at origin:
   CM at (0,0)

Uniform solid sphere, radius R = 3 cm, centered at (2,5):
   CM at (2,5) -- same as its geometric center` },
  { section: "Centre of Mass — Continuous Bodies", title: "Centre of mass of a composite (combined) body",
    explanation: "For a body made of several parts, each with a known mass and a known individual CM, the CM of the whole body is found by treating each part as a single point mass concentrated at its own CM, then applying the discrete n-particle formula to those point masses.",
    example: `Uniform rod: mass 3 kg, lies along x-axis from x=0 to x=4 m
   -> its own CM is at x=2 m
Uniform disc: mass 2 kg, attached at the rod's end so its
   center is at x=5 m

Combined CM: x_cm = (3x2 + 2x5)/(3+2) = (6+10)/5 = 16/5 = 3.2 m` },

  // ── Motion of the Centre of Mass ────────────────────────────────────────────
  { section: "Motion of the Centre of Mass", title: "Velocity of the centre of mass",
    explanation: "Differentiating the CM position formula with respect to time gives the velocity of the centre of mass: v_cm = (sum of mi.vi)/M, where vi is the velocity of the i-th particle and M is the total mass. This is simply the mass-weighted average of the individual particle velocities.",
    example: `m1 = 1 kg moving at v1 = 4 m/s (+x)
m2 = 3 kg moving at v2 = 2 m/s (+x)

v_cm = (1x4 + 3x2)/(1+3) = (4+6)/4 = 10/4 = 2.5 m/s` },
  { section: "Motion of the Centre of Mass", title: "Total momentum of a system of particles",
    explanation: "The total (linear) momentum of a system of particles equals the sum of the individual momenta, which also equals the total mass times the velocity of the centre of mass: P = sum of (mi.vi) = M.v_cm. So a system of particles behaves, as far as overall translational momentum goes, exactly like a single particle of mass M located at the CM and moving with velocity v_cm.",
    example: `Using the system above (m1=1kg @ 4 m/s, m2=3kg @ 2 m/s):

P = m1v1 + m2v2 = 1(4) + 3(2) = 4 + 6 = 10 kg.m/s

Check via CM: M.v_cm = 4 x 2.5 = 10 kg.m/s  <- matches` },
  { section: "Motion of the Centre of Mass", title: "Newton's second law for a system of particles",
    explanation: "Internal forces between the particles of a system occur in equal-and-opposite pairs (Newton's third law) and cancel out completely when summed over the whole system. Only the net EXTERNAL force determines how the CM accelerates: F_ext = M.a_cm, where a_cm is the acceleration of the centre of mass. This holds no matter how complicated the internal forces (springs, collisions, explosions) are.",
    example: `Two blocks, m1=2kg and m2=3kg (M=5kg), connected by a spring
(an internal force only). A net external force of 10 N acts
on the system.

a_cm = F_ext/M = 10/5 = 2 m/s^2
(The spring's internal push/pull never appears in this equation)` },
  { section: "Motion of the Centre of Mass", title: "Motion of the centre of mass under gravity — explosion in flight",
    explanation: "If the only external force on a system is gravity, its CM moves exactly like a single projectile of mass M under gravity (a_cm = g, downward), no matter what internal forces act between its parts. So if a projectile explodes in mid-air, the fragments fly off in different directions, but their combined centre of mass keeps moving along the SAME parabolic trajectory the unexploded projectile would have followed -- because the explosion's forces are purely internal.",
    example: `A shell fired as a projectile explodes at the top of its path
into several fragments.

Regardless of each fragment's individual path:
   a_cm = g = 9.8 m/s^2, downward, throughout

So the CM keeps tracing the ORIGINAL parabolic path and would
"land" at the same point the intact shell would have.` },

  // ── Momentum Conservation Applications ─────────────────────────────────────
  { section: "Momentum Conservation Applications", title: "Conservation of momentum for a system of particles",
    explanation: "When the net external force on a system is zero, F_ext = M.a_cm = 0, so the CM moves with constant velocity and the total momentum P = M.v_cm stays constant -- this is the law of conservation of momentum for a system of particles. Momentum can be exchanged between the parts of the system through internal forces, but the total is unchanged.",
    example: `Two particles, m1=1kg and m2=1kg.
Initial: m1 moves at u1=5 m/s, m2 is at rest (u2=0)
Initial momentum = 1(5) + 1(0) = 5 kg.m/s

They collide and stick together (combined mass 2 kg) moving
at a common velocity v:
   2v = 5  ->  v = 2.5 m/s
Final momentum = 2 x 2.5 = 5 kg.m/s  <- matches initial` },
  { section: "Momentum Conservation Applications", title: "Recoil of a gun firing a bullet",
    explanation: "A gun and bullet, initially at rest together, form a system with zero total initial momentum and (ignoring external horizontal forces) zero net external force during firing. By conservation of momentum, the gun must recoil backward with momentum exactly equal and opposite to the bullet's forward momentum.",
    example: `Gun mass M = 4 kg, bullet mass m = 0.02 kg, fired at v = 300 m/s

Initial momentum = 0 = M.V + m.v
(V = recoil velocity of gun; bullet direction taken as positive)

V = -m.v/M = -(0.02x300)/4 = -6/4 = -1.5 m/s
(Gun recoils at 1.5 m/s, opposite to the bullet's direction)` },
  { section: "Momentum Conservation Applications", title: "Explosion of a body into fragments",
    explanation: "When a body at rest (or in flight) explodes into two or more fragments due to purely internal forces, the total momentum of all the fragments immediately after the explosion equals the total momentum immediately before (zero, if the body was at rest). This lets the velocity of one fragment be found once the others are known.",
    example: `A 10 kg bomb at rest explodes into two fragments: 4 kg and 6 kg.
The 4 kg fragment moves at 6 m/s in the +x direction.
Find the velocity of the 6 kg fragment.

Conservation: 0 = (4)(6) + (6)(v2)
24 = -6.v2   ->   v2 = -4 m/s

(The 6 kg fragment moves at 4 m/s in the -x direction,
opposite to the first fragment)` },
  { section: "Momentum Conservation Applications", title: "Two people pushing off each other on frictionless ice",
    explanation: "Two people standing together at rest on frictionless ice form a system with zero initial momentum. When they push each other apart, the push is an internal action-reaction force pair (Newton's third law), so total momentum stays zero -- they move off in opposite directions with speeds inversely proportional to their masses.",
    example: `Person A (mass 50 kg) and Person B (mass 70 kg), both
initially at rest, push apart.
If A moves off at 2.8 m/s, find B's speed.

Conservation: 0 = mA.vA - mB.vB (opposite directions)
   -> mA.vA = mB.vB
50 x 2.8 = 70 x vB   ->   140 = 70.vB   ->   vB = 2 m/s

(Lighter person A moves faster; heavier person B moves
slower, in the opposite direction)` },
];

const S = {
  page:  { minHeight:"100vh", background:"linear-gradient(135deg,#0f172a,#1a1a2e)", fontFamily:"'Segoe UI',sans-serif", color:"#e2e8f0", padding:"18px 16px 80px",
    userSelect:"none", WebkitUserSelect:"none", MozUserSelect:"none", msUserSelect:"none" },
  back:  { color:"#94a3b8", fontSize:13, textDecoration:"none" },
  card:  { background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.11)", borderRadius:14, padding:"18px", marginBottom:16 },
  chip:  (active, color) => ({
    background: active ? color : "rgba(255,255,255,0.08)",
    color: active ? "#fff" : "#94a3b8",
    border:"none", borderRadius:20, padding:"6px 14px", fontSize:12.5, fontWeight:700, cursor:"pointer", marginRight:8, marginBottom:8,
  }),
  tag: (color) => ({ background:color, color:"#fff", borderRadius:20, padding:"2px 10px", fontSize:11, fontWeight:700, display:"inline-block" }),
  pre: { background:"#0b1120", border:"1px solid rgba(255,255,255,0.08)", borderRadius:10, padding:"12px 14px", fontSize:12.5, lineHeight:1.6, color:"#a5f3b8", overflowX:"auto", fontFamily:"'Fira Code','Consolas',monospace", whiteSpace:"pre" },
  option: (bg) => ({ display:"block", width:"100%", textAlign:"left", background:bg, border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, padding:"9px 12px", color:"#e2e8f0", fontSize:13, marginBottom:7, cursor:"pointer" }),
  iconBtn: (bg) => ({ background:bg, border:"none", borderRadius:8, width:30, height:30, fontSize:14, lineHeight:1, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", flex:"none" }),
};

// ── One interactive objective question — click an option to check it ──────────
function QuizQuestion({ item, index, onAnswer }) {
  const [selected, setSelected] = useState(null);
  const answered = selected !== null;

  const pick = (i) => {
    if (answered) return;
    setSelected(i);
    onAnswer && onAnswer(i === item.correct);
  };

  return (
    <div style={S.card}>
      <span style={S.tag(SECTION_COLORS[item.section])}>{item.section}</span>
      <span style={{ fontSize:11, color:"#64748b", marginLeft:8 }}>{item.topic}</span>
      <div style={{ fontWeight:700, fontSize:14, color:"#fff", marginTop:8, marginBottom:10 }}>{index + 1}. {item.q}</div>
      {item.options.map((opt, i) => {
        let bg = "rgba(255,255,255,0.05)";
        if (answered) {
          if (i === item.correct) bg = "rgba(34,197,94,0.28)";
          else if (i === selected) bg = "rgba(239,68,68,0.28)";
        }
        return (
          <button key={i} style={S.option(bg)} onClick={() => pick(i)} disabled={answered}>
            {String.fromCharCode(65 + i)}. {opt}
          </button>
        );
      })}
      {answered && (
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:8, lineHeight:1.6 }}>
          {selected === item.correct ? "✅ Correct — " : "❌ Not quite — "}{item.explain}
        </div>
      )}
    </div>
  );
}

export default function NeetPhysicsCh6Page() {
  const [mode, setMode] = useState("guide"); // "guide" | "practice"
  const [activeSection, setActiveSection] = useState("All");
  const [score, setScore] = useState({ correct: 0, answered: 0 });

  // ── Bookmarks — same 🔖 pattern used elsewhere on the site, persisted locally ──
  const [bookmarks, setBookmarks] = useState(() => {
    try {
      const raw = window.localStorage.getItem("neetPhysicsCh6Bookmarks");
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [showBookmarkedOnly, setShowBookmarkedOnly] = useState(false);

  useEffect(() => {
    try { window.localStorage.setItem("neetPhysicsCh6Bookmarks", JSON.stringify(bookmarks)); } catch { /* ignore */ }
  }, [bookmarks]);

  const toggleBookmark = (title) => {
    setBookmarks(prev => prev.includes(title) ? prev.filter(t => t !== title) : [...prev, title]);
  };

  // ── Read aloud — same speechSynthesis pattern used on Reading Notes / TNPSC ──
  const [speakingIdx, setSpeakingIdx] = useState(null);

  useEffect(() => () => { window.speechSynthesis && window.speechSynthesis.cancel(); }, []);

  const speakConcept = (c, idx) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    if (speakingIdx === idx) { setSpeakingIdx(null); return; }
    const u = new window.SpeechSynthesisUtterance(`${c.title}. ${c.explanation}`);
    u.rate = 0.95;
    u.onend = () => setSpeakingIdx(null);
    u.onerror = () => setSpeakingIdx(null);
    window.speechSynthesis.speak(u);
    setSpeakingIdx(idx);
  };

  // ── Share — native share sheet, falling back to WhatsApp like the other pages ──
  const shareConcept = (c, idx) => {
    const url = `${window.location.origin}${window.location.pathname}#concept-${idx}`;
    const text = `🧪 NEET Physics — ${c.title}\n\n${c.explanation}\n\n${url}`;
    if (navigator.share) {
      navigator.share({ title: `NEET Physics — ${c.title}`, text, url }).catch(() => {});
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    }
  };

  const filtered = useMemo(() => {
    let list = activeSection === "All" ? CONCEPTS : CONCEPTS.filter(c => c.section === activeSection);
    if (showBookmarkedOnly) list = list.filter(c => bookmarks.includes(c.title));
    return list;
  }, [activeSection, showBookmarkedOnly, bookmarks]);

  const filteredQuestions = useMemo(() => (
    activeSection === "All" ? NEET_PHYSICS_CH6_QUESTIONS : NEET_PHYSICS_CH6_QUESTIONS.filter(q => q.section === activeSection)
  ), [activeSection]);

  const handleModeChange = (m) => {
    setMode(m);
    setScore({ correct: 0, answered: 0 });
  };

  const handleAnswer = (isCorrect) => {
    setScore(s => ({ correct: s.correct + (isCorrect ? 1 : 0), answered: s.answered + 1 }));
  };

  return (
    <div
      style={S.page}
      onCopy={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
      onContextMenu={(e) => e.preventDefault()}
    >
      <Link to="/neet-physics" style={S.back}>← Back to NEET Physics Menu</Link>

      <div style={{ textAlign:"center", padding:"14px 0 10px" }}>
        <div style={{ fontSize:26, fontWeight:800, color:"#fff" }}>🧪 NEET Physics — Chapter 6</div>
        <div style={{ fontSize:15, fontWeight:700, color:"#fbbf24", marginTop:4 }}>Centre of Mass and System of Particles</div>
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:6 }}>
          NCERT Class 11, Systems of Particles & Rotational Motion (CM & momentum half) · {CONCEPTS.length} concepts explained · {NEET_PHYSICS_CH6_QUESTIONS.length} practice questions · NOT YET human-reviewed, verify against NCERT before exam use
        </div>
      </div>

      {/* Guide / Practice mode toggle */}
      <div style={{ textAlign:"center", margin:"14px 0 8px" }}>
        <button style={S.chip(mode==="guide", "#0f766e")} onClick={() => handleModeChange("guide")}>📘 Concept Guide</button>
        <button style={S.chip(mode==="practice", "#b45309")} onClick={() => handleModeChange("practice")}>🎯 Practice Questions ({NEET_PHYSICS_CH6_QUESTIONS.length})</button>
      </div>

      {/* Section filter chips */}
      <div style={{ textAlign:"center", margin:"6px 0 18px" }}>
        <button style={S.chip(activeSection==="All", "#334155")} onClick={() => setActiveSection("All")}>All</button>
        {SECTIONS.map(s => (
          <button key={s} style={S.chip(activeSection===s, SECTION_COLORS[s])} onClick={() => setActiveSection(s)}>{s}</button>
        ))}
      </div>

      {mode === "practice" ? (
        <>
          {score.answered > 0 && (
            <div style={{ textAlign:"center", fontSize:13, fontWeight:700, color:"#fbbf24", marginBottom:14 }}>
              Score: {score.correct} / {score.answered} answered correctly
            </div>
          )}
          {filteredQuestions.map((item, i) => (
            <QuizQuestion key={i} item={item} index={i} onAnswer={handleAnswer} />
          ))}
        </>
      ) : (
      <>
      {/* ── Bookmark filter ── */}
      <div style={{ textAlign:"center", margin:"0 0 14px" }}>
        <button style={S.chip(showBookmarkedOnly, "#db2777")} onClick={() => setShowBookmarkedOnly(v => !v)}>
          🔖 {showBookmarkedOnly ? "Showing Bookmarked" : "Bookmarked"} ({bookmarks.length})
        </button>
      </div>

      {/* ── Quick reference table ── */}
      <div style={S.card}>
        <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginBottom:12 }}>📋 Quick Reference Table</div>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead>
              <tr style={{ borderBottom:"2px solid rgba(255,255,255,0.15)" }}>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>#</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Section</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Topic</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => (
                <tr key={i} style={{ borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
                  <td style={{ padding:"7px 8px", color:"#64748b" }}>{i + 1}</td>
                  <td style={{ padding:"7px 8px" }}><span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span></td>
                  <td style={{ padding:"7px 8px" }}>
                    <a
                      href={`#concept-${i}`}
                      style={{ color:"#38bdf8", textDecoration:"underline", fontWeight:600 }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = "#7dd3fc"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = "#38bdf8"; }}
                    >
                      {c.title}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Detailed concept cards ── */}
      {filtered.map((c, i) => {
        const isBookmarked = bookmarks.includes(c.title);
        const isSpeaking = speakingIdx === i;
        return (
        <div key={i} id={`concept-${i}`} style={S.card}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8, flexWrap:"wrap" }}>
            <span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span>
            <div style={{ display:"flex", gap:6 }}>
              <button
                title={isSpeaking ? "Stop reading" : "Read aloud"}
                onClick={() => speakConcept(c, i)}
                style={S.iconBtn(isSpeaking ? "#2563eb" : "rgba(37,99,235,0.22)")}
              >{isSpeaking ? "⏹" : "🔊"}</button>
              <button
                title={isBookmarked ? "Remove bookmark" : "Bookmark"}
                onClick={() => toggleBookmark(c.title)}
                style={S.iconBtn(isBookmarked ? "#db2777" : "rgba(219,39,119,0.22)")}
              >{isBookmarked ? "🔖" : "📌"}</button>
              <button
                title="Share"
                onClick={() => shareConcept(c, i)}
                style={S.iconBtn("rgba(255,255,255,0.12)")}
              >↗</button>
            </div>
          </div>
          <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginTop:8 }}>{i + 1}. {c.title}</div>
          <div style={{ fontSize:13.5, color:"#cbd5e1", lineHeight:1.7, marginTop:8 }}>{c.explanation}</div>
          <div style={{ fontSize:11, fontWeight:700, color:"#94a3b8", marginTop:12, marginBottom:6, letterSpacing:0.4 }}>EXAMPLE / WORKED CALCULATION</div>
          <pre style={S.pre}><code>{c.example}</code></pre>
        </div>
        );
      })}
      </>
      )}
    </div>
  );
}
